import {
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Title,
  Tooltip,
} from "chart.js";
import "chartjs-adapter-luxon";
import { Scatter } from "react-chartjs-2";
import {
  convertCurrent,
  friendlyWindUnitName,
  graphHeight,
} from "../util/util";
import { SpotData, TideWindData, WindowSize } from "../datastructures";
import React from "react";
import InfoOverlay from "./InfoOverlay";

ChartJS.register(
  TimeScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Filler,
  Legend,
);

const CHART_COLORS = {
  red: "rgb(255, 99, 132)",
  orange: "rgb(255, 159, 64)",
  yellow: "rgb(255, 205, 86)",
  green: "rgb(75, 192, 192)",
  blue: "rgb(54, 162, 235)",
  purple: "rgb(153, 102, 255)",
  grey: "rgb(201, 203, 207)",
};

ChartJS.defaults.font.size = 12;

const round2 = (num: number): number =>
  Math.round(num * 100 + Number.EPSILON) / 100;

interface UpwindCurrVelocityGraphProperties {
  day: string;
  windowSize: WindowSize;
  tideWindData: TideWindData[];
  selectedSpotData: SpotData;
  windUnits: string;
}

export const UpwindCurrVelocityGraph: React.FC<
  UpwindCurrVelocityGraphProperties
> = ({ day, windowSize, tideWindData, selectedSpotData, windUnits }) => {
  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        suggestedMin: convertCurrent(0.5144444, windUnits),
        suggestedMax: convertCurrent(0.5144444, windUnits),
        distribution: "linear",
        title: {
          display: true,
          text:
            "Stroomsnelheid recht tegen de wind in (" +
            friendlyWindUnitName(windUnits) +
            ")",
        },

        ticks: {
          beginAtZero: true,
          min: -1,
          max: 1,
        },

        grid: {
          color: function (context: any) {
            if (context.tick.value > 0) {
              return CHART_COLORS.green;
            } else if (context.tick.value < 0) {
              return CHART_COLORS.red;
            }

            return "#000000";
          },
        },
      },
      x: {
        type: "time",
        title: {
          display: true,
          text: "Tijd",
        },
        distribution: "linear",
        bounds: "data",
        time: {
          displayFormats: {
            hour: "H:mm",
            minute: "H:mm",
          },
          tooltipFormat: "H:mm",
        },
        ticks: {
          beginAtZero: false,
          maxRotation: 0,
        },
      },
    },
    plugins: {
      title: { display: false },
      legend: {
        display: false,
      },

      tooltip: {
        callbacks: {
          label: function (context: any) {
            return (
              context.label +
              ": " +
              context.parsed.y +
              " " +
              friendlyWindUnitName(windUnits)
            );
          },
        },
      },
    },
  };

  const graphDataSet = tideWindData?.map((row) => ({
    x: row["validtime"] * 1000,
    y: round2(convertCurrent(row["upwind_cur_speed"], windUnits)),
  }));

  const graphData = graphDataSet
    ? {
        datasets: [
          {
            backgroundColor: "rgba(35, 134, 239, 0)",
            borderColor: "rgba(35, 134, 239, 1)",
            data: graphDataSet,
            label: "Stroomsnelheid recht tegen de wind in",

            pointHitRadius: 10,
            pointRadius: 1,
            showLine: true,
          },
        ],
      }
    : null;

  return (
    <>
      <br />
      <div
        style={{
          textAlign: "center",
          marginBottom: "0.5rem",
          fontSize: 12,
          fontWeight: "bold",
        }}
      >
        Stroomsnelheid tegen de wind in {selectedSpotData.name} {day}{" "}
        <InfoOverlay>
          <p>
            Omdat kitesurfen relatief snel gaat, maar niet heel hoog aan de
            wind, is het vooral belangrijk om te weten hoeveel hoogte de
            stroming je kost of oplevert. Stroming die haaks op de wind staat
            kun je vrijwel verwaarlozen.
          </p>
          <p>Stroming tegen de wind in heeft 2 voordelen:</p>
          <ul>
            <li>Je krijgt hoogte kado</li>
            <li>
              Je hebt meer druk in je kite omdat je er ook de stroomsnelheid aan
              schijnbare wind bij krijgt.
            </li>
          </ul>
          <p>
            De wind tegen stroming grafiek is zo gemaakt dat alleen de component
            van de stroming recht tegen de wind in mee telt. Als de grafiek in
            het groen staat (positieve getallen), drukt de stroming je tegen de
            wind in. Dat geeft een leukere sessie. Als hij in het rood staat
            (negatieve getallen) wordt je met de wind mee gedrukt en kun je een
            minder leuke sessie verwachten.
          </p>
          <p>
            Last but not least:
            <br />
            Als zwemmer maakt het ineens wel uit of er stroming haaks op de wind
            staat.
            <br />
            <b>
              Gebruik deze grafiek dus niet bij het inschatten van
              veiligheidsrisico&apos;s!!
            </b>
          </p>
        </InfoOverlay>
      </div>

      <div
        style={{
          height: graphHeight(windowSize),
          position: "relative",
        }}
      >
        {graphData != null ? (
          <Scatter options={options} data={graphData} />
        ) : (
          "Loading tide graph data"
        )}
      </div>
    </>
  );
};
